import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleSlugs, articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'nav' | 'categories' | 'guides'>('nav');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2">
          <span className={`font-heading text-xl md:text-2xl font-bold transition-colors ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
            Superbuy<span className="text-primary-500">Sheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <a href="#categories" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Categories</a>
          <a href="#what-is" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">About</a>
          <a href="#how-it-works" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">How It Works</a>
          <a href="#faq" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">FAQ</a>
          <Link to="/article/superbuy-sheet-guide" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Guide</Link>
          <Link to="/article/superbuy-sheet-beginners" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Beginners</Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-3 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Visit Main Platform
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          style={{ color: scrolled ? undefined : 'white' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 fixed top-16 left-0 w-full h-[calc(100vh-64px)] overflow-y-auto z-40">
          <div className="px-4 py-3 flex items-center gap-1 border-b border-background-200/50">
            {[
              { key: 'nav' as const, label: 'Menu', icon: 'ri-menu-line' },
              { key: 'categories' as const, label: 'Categories', icon: 'ri-grid-line' },
              { key: 'guides' as const, label: 'Guides', icon: 'ri-book-open-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setMobileTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  mobileTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {mobileTab === 'nav' && (
            <div className="px-4 py-4 flex flex-col gap-1">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-2 px-2">Navigation</p>
              <a href="#categories" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600"><i className="ri-grid-line"></i></div>
                Browse Categories
              </a>
              <a href="#what-is" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600"><i className="ri-information-line"></i></div>
                About Superbuy Sheet
              </a>
              <a href="#how-it-works" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600"><i className="ri-guide-line"></i></div>
                How It Works
              </a>
              <a href="#faq" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600"><i className="ri-question-line"></i></div>
                FAQ
              </a>

              <div className="my-4 border-t border-background-200/50"></div>

              <div className="flex flex-col gap-2 px-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Superbuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="w-full py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          )}

          {mobileTab === 'categories' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-3 px-2">All 14 Categories</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="mt-5 border-t border-background-200/50 pt-4">
                <div className="flex flex-col gap-2">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    Superbuy Sheet
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="w-full py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    Buy It Now
                  </a>
                </div>
              </div>
            </div>
          )}

          {mobileTab === 'guides' && (
            <div className="px-4 py-4">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-3 px-2">SEO Guides & Articles</p>
              <div className="flex flex-col gap-2">
                {articleSlugs.map((slug) => {
                  const a = articles[slug];
                  if (!a) return null;
                  return (
                    <Link
                      key={slug}
                      to={`/article/${slug}`}
                      onClick={closeMobile}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                        <i className="ri-article-line"></i>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-xs text-foreground-500">{a.badge}</span>
                        <p className="text-sm font-medium text-foreground-800 leading-tight truncate">{a.title}</p>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-400 ml-auto flex-shrink-0"></i>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}